// kitsView — view-model compartido entre la página pública de kits (grid /kits) y el detalle
// (/kit/:codigo). Fuente ÚNICA: RPC public.cliente_get_kits_con_productos() (anon-safe), la misma
// que lee la Auditoría de kits. Cualquier cambio en /admin/kits se refleja acá en vivo.
//
// Antes: /kits (Kits.jsx) leía productos y precios HARDCODEADOS de src/data/kits.js → quedaban
// desincronizados de la DB. El detalle ya traía productos reales pero los precios eran estáticos.
// Este módulo centraliza el mapeo DB → forma que consumen las cards y el detalle.
//
// Ahorro "vs súper": tachado = Σ (precio_mercado × cantidad) de los productos NO regalo. El
// precio_mercado es la referencia de góndola que carga Nico por producto. Si un producto no tiene
// precio_mercado, cae a precio_publico como piso (no infla el ahorro). No se fuerza un mínimo — se
// muestra el ahorro real; si no hay ahorro, la línea no se muestra.

import { consolidarProductosKit } from './kitDisplay'

const GRUPO_POR_TIER = { essential: 'essential', premium: 'premium', comercial: 'comercial' }
const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }
const ORDEN_GRUPO = { essential: 0, premium: 1, comercial: 2 }

// codigo DB usa guion_bajo (hogar_plus); las rutas v5 usan guion (hogar-plus).
const aRuta = (c) => String(c || '').replace(/_/g, '-')

// Presentación por unidad, limpia para el card: saca dimensiones entre paréntesis
// ("(99mm × 71mm × 20mm)") y el "cada uno" redundante. NO multiplica por cantidad — el
// prefijo "N×" del nombre ya comunica la cantidad (evita el doble "2× ... × 2").
export function limpiarPresentacion(pres) {
  if (!pres) return ''
  return String(pres)
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s+cada uno\b/gi, '')
    .replace(/\s+c\/u\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

export function kitViewModel(dbKit) {
  const productos = Array.isArray(dbKit.productos) ? dbKit.productos : []
  const reales = productos.filter((p) => !p.es_regalo)

  const precio = Number(dbKit.precio_susc) || 0
  const precioUnica = Number(dbKit.precio_unica) || 0

  const tachado = reales.reduce(
    (s, p) => s + (Number(p.precio_mercado ?? p.precio_publico) || 0) * Number(p.cantidad || 1),
    0,
  )
  const ahorro = tachado > precio ? Math.round(tachado - precio) : 0
  const ahorroPct = tachado > 0 && ahorro > 0 ? Math.round((ahorro / tachado) * 100) : 0
  // Meses 2 y 3 (se devuelve la diferencia): (3·mensual − única) / 2.
  const precioBonif = precio && precioUnica ? Math.round((3 * precio - precioUnica) / 2) : 0

  const consolidados = consolidarProductosKit(reales)
  const productosDisplay = consolidados.map((p) => ({
    real: !p.es_consolidado,
    sku: p.sku || null,
    cantidad: p.es_consolidado ? 1 : Number(p.cantidad || 1),
    cant: p.es_consolidado ? '' : `${Number(p.cantidad || 1)}×`,
    nombre: p.nombre,
    // Presentación por UNIDAD y limpia. El "N×" ya indica la cantidad → no se multiplica
    // (así no queda "2× ... 2 L × 2").
    det: p.es_consolidado
      ? (p.presentacion || '')
      : (limpiarPresentacion(p.presentacion) || p.marca || ''),
    imagen: p.imagen_url || null,
    marca: p.marca || null,
  }))

  return {
    codigo: aRuta(dbKit.codigo),
    codigoDb: dbKit.codigo,
    nombre: dbKit.nombre,
    tier: dbKit.tier,
    tierLabel: TIER_LABEL[dbKit.tier] || dbKit.tier,
    grupo: GRUPO_POR_TIER[dbKit.tier] || dbKit.tier,
    premium: dbKit.tier === 'premium',
    destacado: !!dbKit.destacado,
    personas: dbKit.personas || '',
    descripcion: dbKit.descripcion || '',
    imagen: dbKit.imagen_url || null,
    sinStock: !!dbKit.sin_stock,
    precio,
    precioUnica,
    tachado: Math.round(tachado),
    ahorro,
    ahorroPct,
    precioBonif,
    productos: productosDisplay,
    nProductos: productosDisplay.length,
  }
}

// Ordena los kits para mostrar: essential antes que premium, luego por precio ascendente.
export function ordenarKitsView(vms) {
  return [...vms].sort(
    (a, b) => (ORDEN_GRUPO[a.grupo] ?? 9) - (ORDEN_GRUPO[b.grupo] ?? 9) || a.precio - b.precio,
  )
}
